import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Stack from 'components/core/Stack';
import Grid from 'components/core/Grid';
import Input from 'components/core/Input';
import Button from 'components/core/Button';
import ToggleButtonGroup from 'components/core/ToggleButtonGroup';
import LookupTableInput from 'components/LookupTableInput';
import eventActions from 'actions/eventActions';
import lookupTableActions from 'actions/lookupTableActions';

const initialState = {
    name: '',
    description: '',
    note: '',
    type: null,
    startDate: '',
    endDate: '',
    dateSpecificity: 'yyyy-mm-dd',
    locations: []
};

const EventForm = ({ onSubmit, initialValues }) => {
    const dispatch = useDispatch();
    const [event, setEvent] = useState(initialValues ? {
        name: initialValues?.get?.('name') || initialValues?.name || '',
        description: initialValues?.get?.('description') || initialValues?.description || '',
        note: initialValues?.get?.('note') || initialValues?.note || '',
        type: initialValues?.get?.('type') || initialValues?.type || null,
        startDate: initialValues?.get?.('startDate') || initialValues?.startDate || '',
        endDate: initialValues?.get?.('endDate') || initialValues?.endDate || '',
        dateSpecificity: initialValues?.get?.('dateSpecificity') || initialValues?.dateSpecificity || 'yyyy-mm-dd',
        locations: initialValues?.get?.('locations') || initialValues?.locations || []
    } : initialState);
    const [errors, setErrors] = useState({});
    const options = useSelector(({ lookupTable }) => lookupTable.options);

    const handleSave = async () => {

        setErrors({});
        
        if (!event.name) {
            setErrors(prev => ({ ...prev, name: 'Name is required.' }));
            return;
        }
        if (!event.type) {
            setErrors(prev => ({ ...prev, type: 'Event type is required.' }));
            return;
        }
        if (!event.startDate) {
            setErrors(prev => ({ ...prev, startDate: 'Start date is required.' }));
            return;
        }
        if (!event.endDate) {
            setErrors(prev => ({ ...prev, endDate: 'End date is required.' }));
            return;
        }
        if (new Date(event.startDate) > new Date(event.endDate)) {
            setErrors(prev => ({ ...prev, endDate: 'End date must be after start date.' }));
            return;
        }

        try {
            const eventData = {
                ...event,
                type: event.type?.id,
                locations: event.locations?.map(location => location?.id || location) || []
            };
            
            if (initialValues?.id || initialValues?.get?.('id')) {
                eventData.objectId = initialValues.id || initialValues.get('id');
            }
            
            const savedEvent = await dispatch(eventActions.save(eventData));
            await dispatch(lookupTableActions.fetchAllOptions());
            onSubmit(savedEvent.value);
        } catch (err) {
            setErrors(err.inner?.reduce((acc, curr) => {
                acc[curr.path] = curr.message;
                return acc;
            }, {}) || {});
        }
    };

    const handleFieldChange = (field, value) => {
        setEvent(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const dateSpecificityOptions = [
        { value: 'yyyy-mm-dd', label: 'Day' },
        { value: 'yyyy-mm', label: 'Month' },
        { value: 'yyyy', label: 'Year' },
    ]


    return (
        <Grid container spacing={4}>

            <Grid item xs={12}>
                <Input
                    label="Name"
                    value={event.name}
                    onChange={(value) => handleFieldChange('name', value)}
                    error={errors.name}
                    required
                />
            </Grid>

            <Grid item xs={12}>
                <LookupTableInput
                    label="Event Type"
                    value={event.type}
                    onChange={(value) => handleFieldChange('type', value)}
                    options={options.eventTypes}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.type}
                    required
                />
            </Grid>

            <Grid item xs={12}>
                <LookupTableInput
                    label="Locations"
                    value={event.locations}
                    onChange={(value) => handleFieldChange('locations', value)}
                    options={options.locations}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.locations}
                    multiple
                />
            </Grid>

            <Grid item xs={12}>
                <ToggleButtonGroup
                    label="Date Specificity"
                    value={event.dateSpecificity}
                    onChange={(value) => handleFieldChange('dateSpecificity', value)}
                    options={dateSpecificityOptions}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Input
                    label="Start Date"
                    type="date"
                    format={event.dateSpecificity}
                    value={event.startDate}
                    onChange={(value) => handleFieldChange('startDate', value)}
                    error={errors.startDate}
                    required
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Input
                    label="End Date"
                    type="date"
                    format={event.dateSpecificity}
                    value={event.endDate}
                    onChange={(value) => handleFieldChange('endDate', value)}
                    error={errors.endDate}
                    required
                />
            </Grid>

            <Grid item xs={12}>
                <Input
                    label="Description"
                    multiline
                    rows={4}
                    value={event.description}
                    onChange={(value) => handleFieldChange('description', value)}
                    error={errors.description}
                />
            </Grid>

            <Grid item xs={12}>
                <Input
                    label="Note"
                    multiline
                    rows={4}
                    value={event.note}
                    onChange={(value) => handleFieldChange('note', value)}
                    error={errors.note}
                />
            </Grid>

            <Grid item xs={12}>
                <Button onClick={handleSave}>
                    {initialValues ? 'Update' : 'Create'} Event
                </Button>
            </Grid>
        </Grid>
    );
};

export default EventForm;